import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarClock, Mail, ShieldCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { acceptTeamInvitation, declineTeamInvitation, getTeamInvitation } from '@/services/team/team-invitations.service';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function formatDate(value?: string) {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toLocaleString();
}

function formatTimeUntil(value?: string) {
  if (!value) {
    return undefined;
  }
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) {
    return undefined;
  }
  const diff = target - Date.now();
  if (diff <= 0) {
    return 'Expired';
  }
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainingMinutes = minutes % 60;
    return `${hours} hour${hours === 1 ? '' : 's'}${remainingMinutes ? ` ${remainingMinutes} min` : ''}`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days} day${days === 1 ? '' : 's'}${remainingHours ? ` ${remainingHours} hr` : ''}`;
}

function getInitials(value?: string) {
  if (!value) {
    return 'TW';
  }

  const parts = value
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return value.slice(0, 2).toUpperCase();
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

function extractErrorMessage(error: unknown, fallback: string) {
  const message =
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error instanceof Error ? error.message : undefined);
  return message ?? fallback;
}

export default function TeamInvitationAcceptPage() {
  const params = useQueryParams();
  const navigate = useNavigate();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const { t, i18n } = useTranslation();

  const token = params.get('token') ?? '';
  const emailQuery = params.get('email') ?? '';
  const languageParam = params.get('lang') ?? params.get('language');

  const [email, setEmail] = useState(emailQuery);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [localStatus, setLocalStatus] = useState<'accepted' | 'declined' | null>(null);

  useEffect(() => {
    setEmail(emailQuery);
  }, [emailQuery]);

  const invitationQuery = useQuery({
    queryKey: ['team-invitation', token],
    queryFn: () => getTeamInvitation(token),
    enabled: Boolean(token),
    retry: 1,
  });

  const invitationLanguage = normalizeLanguageCode(invitationQuery.data?.language);
  const preferredLanguage =
    normalizeLanguageCode(languageParam) ?? invitationLanguage ?? normalizeLanguageCode(i18n.language ?? i18n.resolvedLanguage);

  useEffect(() => {
    if (preferredLanguage && i18n.language !== preferredLanguage) {
      void i18n.changeLanguage(preferredLanguage);
    }
  }, [i18n, preferredLanguage]);

  const acceptMutation = useMutation({
    mutationFn: () => acceptTeamInvitation({ token, email, language: preferredLanguage }),
    onSuccess: (response) => {
      setLocalStatus('accepted');
      toast.success(response.message);
      const redirect = `/customer/login${email ? `?email=${encodeURIComponent(email)}` : ''}`;
      navigate(redirect, { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(
        extractErrorMessage(
          error,
          t('team.invitations.accept.toastAcceptError', 'Unable to accept invitation.')
        )
      );
    },
  });

  const declineMutation = useMutation({
    mutationFn: () =>
      declineTeamInvitation({
        token,
        email,
        reason: declineReason,
        language: preferredLanguage,
      }),
    onSuccess: (response) => {
      setLocalStatus('declined');
      toast.success(response.message);
    },
    onError: (error: unknown) => {
      toast.error(
        extractErrorMessage(
          error,
          t('team.invitations.accept.toastDeclineError', 'Unable to decline invitation.')
        )
      );
    },
  });

  const invitation = invitationQuery.data;
  const invitationStatus = localStatus ?? invitation?.status;
  const isPending = invitationStatus === 'pending';
  const isActionInFlight = acceptMutation.isPending || declineMutation.isPending;
  const disableActions = !token || !email || !isPending || isActionInFlight;

  const showError = !token || invitationQuery.isError;
  const missingInfoMessage = t(
    'team.invitations.accept.missingInfo',
    'This invitation link is missing required information.'
  );
  const defaultInvitationError = t(
    'team.invitations.accept.defaultError',
    'Invitation not found or no longer available.'
  );
  const errorMessage = !token
    ? missingInfoMessage
    : extractErrorMessage(invitationQuery.error, defaultInvitationError);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen max-w-4xl items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <Card className="w-full overflow-hidden border border-border/40 bg-card shadow-xl">
          {showError ? (
            <div className="space-y-6 p-8 md:p-12">
              <div className="space-y-2">
                <Badge variant="destructive" className="w-fit uppercase tracking-wide">
                  {t('team.invitations.accept.issueBadge', 'Invitation issue')}
                </Badge>
                <h1 className="text-2xl font-semibold tracking-tight">
                  {t('team.invitations.accept.errorHeading', "We couldn't open this invitation")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {errorMessage}
                  {!token
                    ? null
                    : ` ${t(
                        'team.invitations.accept.errorFollowup',
                        'If you believe this is a mistake, reach out to the workspace owner and request a new link.'
                      )}`}
                </p>
              </div>
              <Alert variant="destructive">
                <AlertDescription>
                  {t(
                    'team.invitations.accept.errorAlert',
                    'Invitation tokens expire or are revoked once they’re used. Ask the team owner to generate a fresh invitation and check that you’re using the latest email they sent.'
                  )}
                </AlertDescription>
              </Alert>
            </div>
          ) : invitationQuery.isPending || !invitation ? (
            <div className="flex flex-col items-center justify-center gap-4 p-16 text-center">
              <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary/40 border-t-transparent" />
              <div className="space-y-1">
                <p className="text-sm font-medium tracking-tight">
                  {t('team.invitations.accept.checkingTitle', 'Checking your invitation…')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'team.invitations.accept.checkingSubtitle',
                    'We’re verifying the workspace and permissions tied to this link.'
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-0 bg-background md:grid-cols-[1.15fr,1fr]">
              <div className="space-y-8 border-b border-border/40 p-8 md:border-b-0 md:border-r md:p-10">
                {(() => {
                  const workspaceName =
                    invitation.providerName ?? provider?.name ?? t('team.invitations.accept.workspaceFallback', 'your workspace');
                  const workspaceInitials = getInitials(workspaceName);
                  const teamDisplayName = invitation.teamName ?? workspaceName;
                  const roleLabel = invitation.roleLabel ?? invitation.role;
                  const timeUntil =
                    formatTimeUntil(invitation.expiresAt) ?? t('team.invitations.accept.timeSoon', 'soon');
                  const expiresOnLabel = invitation.expiresAt ? formatDate(invitation.expiresAt) : undefined;

                  return (
                    <>
                      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-4">
                          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
                            <UserPlus className="h-3.5 w-3.5" />{' '}
                            {t('team.invitations.accept.invitationBadge', 'Invitation')}
                          </span>
                          <div className="space-y-3">
                            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                              {t('team.invitations.accept.joinHeading', 'Join {{team}}', {
                                team: teamDisplayName,
                              })}
                            </h1>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                              {t(
                                'team.invitations.accept.joinSubtitle',
                                'Step into the shared workspace for {{workspace}}. Accept the invitation to unlock shared seats, billing insights, and automation tools curated for this team.',
                                { workspace: workspaceName }
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="hidden md:flex">
                          {brandLogo ? (
                            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-border/40 bg-muted/40">
                              <img
                                src={brandLogo}
                                alt={`${workspaceName} logo`}
                                className="h-10 w-auto max-w-[56px] object-contain"
                              />
                            </div>
                          ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-xl font-semibold text-primary">
                              {workspaceInitials}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-4">
                          <div className="flex items-center gap-3">
                            <ShieldCheck className="h-5 w-5 text-primary" />
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-primary/80">
                                {t('team.invitations.accept.roleLabel', 'Role')}
                              </p>
                              <p className="text-sm font-semibold text-primary/90">{roleLabel}</p>
                            </div>
                          </div>
                          <p className="mt-3 text-xs text-primary/70">
                            {t(
                              'team.invitations.accept.roleNote',
                              'You’ll join with permissions set by your workspace owner. You can adjust your profile after you sign in.'
                            )}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-4">
                          <div className="flex items-center gap-3">
                            <CalendarClock className="h-5 w-5 text-amber-500" />
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-amber-500">
                                {t('team.invitations.accept.expiresLabel', 'Invitation expires')}
                              </p>
                              <p className="text-sm font-semibold text-foreground">{timeUntil}</p>
                            </div>
                          </div>
                          <p className="mt-3 text-xs text-amber-600 dark:text-amber-300">
                            {expiresOnLabel
                              ? t('team.invitations.accept.confirmBy', 'Confirm by {{date}} to keep your reserved seat.', {
                                  date: expiresOnLabel,
                                })
                              : t('team.invitations.accept.confirmSoon', 'Confirm soon to keep your reserved seat.')}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-5 rounded-2xl border border-border/40 bg-card p-6 shadow-inner">
                        <div className="flex items-start gap-3">
                          <Mail className="mt-0.5 h-5 w-5 text-primary" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              {t('team.invitations.accept.detailsHeading', 'Invitation details')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {(() => {
                                const parts = [
                                  t('team.invitations.accept.sentTo', 'Sent to {{email}}', {
                                    email: invitation.email,
                                  }),
                                ];
                                if (invitation.inviterName) {
                                  parts.push(
                                    t('team.invitations.accept.sentBy', 'by {{name}}', {
                                      name: invitation.inviterName,
                                    })
                                  );
                                }
                                if (invitation.invitedAt) {
                                  parts.push(
                                    t('team.invitations.accept.sentOn', 'on {{date}}', {
                                      date: formatDate(invitation.invitedAt),
                                    })
                                  );
                                }
                                return `${parts.join(' ')}.`;
                              })()}
                            </p>
                          </div>
                        </div>
                        <Separator className="bg-border/60" />
                        <div className="grid gap-4 sm:grid-cols-2 text-sm">
                          <div className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {t('team.invitations.accept.workspaceLabel', 'Workspace')}
                            </p>
                            <p className="font-medium text-foreground">{workspaceName}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {t('team.invitations.accept.teamLabel', 'Team')}
                            </p>
                            <p className="font-medium text-foreground">{teamDisplayName}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {t('team.invitations.accept.statusLabel', 'Status')}
                            </p>
                            <p className="font-medium capitalize text-foreground">
                              {invitation.statusLabel ?? invitation.status}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {t('team.invitations.accept.emailOnFileLabel', 'Email on file')}
                            </p>
                            <p className="font-medium text-foreground">{invitation.email}</p>
                          </div>
                        </div>
                        {invitation.message ? (
                          <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-sm italic text-muted-foreground">
                            “{invitation.message}”
                          </div>
                        ) : null}
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="flex flex-col gap-6 bg-card p-8 md:p-10">
                <div className="rounded-2xl border border-primary/15 bg-background p-4 text-sm text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 text-primary" />
                    <p>
                      {t(
                        'team.invitations.accept.confirmEmailNote',
                        'Confirm the email you’ll use to join this workspace. We’ll match it with the invitation before activating your account.'
                      )}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invitation-email">
                    {t('team.invitations.accept.emailLabel', 'Your email')}
                  </Label>
                  <Input
                    id="invitation-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t('team.invitations.accept.emailPlaceholder', 'you@example.com')}
                    disabled={isActionInFlight || !!localStatus}
                    className="h-11 text-base"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'team.invitations.accept.emailNote',
                      'We’ll confirm this email matches the invitation before completing the action.'
                    )}
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl border border-border/40 bg-background p-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-primary" />
                    <span>
                    {t('team.invitations.accept.confirmationNotice', 'Confirmation will be sent to {{email}}.', {
                      email: email || invitation.email,
                    })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <UserPlus className="h-4 w-4 text-primary" />
                    <span>
                      {invitation.inviterName
                        ? t('team.invitations.accept.invitedBy', 'Invited by {{name}}.', {
                            name: invitation.inviterName,
                          })
                        : t('team.invitations.accept.seatProvided', 'Seat provided by the workspace owner.')}
                    </span>
                  </div>
                  {invitation.expiresAt ? (
                    <div className="flex items-center gap-3">
                      <CalendarClock className="h-4 w-4 text-primary" />
                      <span>
                        {t('team.invitations.accept.expiresOn', 'Expires on {{date}}.', {
                          date: formatDate(invitation.expiresAt),
                        })}
                      </span>
                    </div>
                  ) : null}
                </div>

                {localStatus === 'declined' ? (
                  <Alert>
                    <AlertDescription>
                      {t(
                        'team.invitations.accept.declinedAlert',
                        'You’ve declined this invitation. You can close this tab or contact the workspace owner if you change your mind.'
                      )}
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="flex flex-col gap-3">
                  <Button
                    type="button"
                    onClick={() => acceptMutation.mutate()}
                    disabled={disableActions || !email}
                    className="h-12 w-full text-base font-semibold tracking-tight shadow-lg shadow-primary/20"
                  >
                    {acceptMutation.isPending
                      ? t('team.invitations.accept.accepting', 'Accepting…')
                      : t('team.invitations.accept.acceptCta', 'Accept invitation')}
                  </Button>
                  {!showDeclineForm ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full border-destructive/40 text-destructive transition-colors hover:bg-destructive/10"
                      onClick={() => setShowDeclineForm(true)}
                      disabled={disableActions}
                    >
                      {t('team.invitations.accept.declineCta', 'Decline')}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-11 w-full"
                      onClick={() => {
                        setShowDeclineForm(false);
                        setDeclineReason('');
                      }}
                      disabled={isActionInFlight}
                    >
                      {t('team.invitations.accept.cancelDecline', 'Cancel decline')}
                    </Button>
                  )}
                </div>

                {showDeclineForm ? (
                  <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="decline-reason">
                        {t(
                          'team.invitations.accept.declinePrompt',
                          'Let the team know why you’re passing (optional)'
                        )}
                      </Label>
                      <Textarea
                        id="decline-reason"
                        value={declineReason}
                        onChange={(event) => setDeclineReason(event.target.value)}
                        placeholder={t(
                          'team.invitations.accept.declinePlaceholder',
                          'Not joining this project right now…'
                        )}
                        rows={3}
                        disabled={isActionInFlight}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => declineMutation.mutate()}
                        disabled={isActionInFlight || !email}
                      >
                        {declineMutation.isPending
                          ? t('team.invitations.accept.declining', 'Declining…')
                          : t('team.invitations.accept.sendDecline', 'Send decline')}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {t(
                          'team.invitations.accept.declineNote',
                          'We’ll notify the workspace owner immediately.'
                        )}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
